/* Provide access to API data */
import { useEffect, useState } from "react";
import { getStatus, getAllENS, getApiData } from "../util/Api";
import web3 from "web3";
const refreshInterval = 10000; // auto refresh, in milliseconds

const WithApi = () => {
  const [transcoderInfo, setTranscoders] = useState([]);
  const [poolInfo, setInfo] = useState([]);
  const [poolStatus, setStatus] = useState([]);
  const [orchInfo, setOrch] = useState({});
  const [paymentInfo, setPayments] = useState([]);
  const [cachedENS, setENSCache] = useState(null);

  // Merges transcoder session data and transcoder info to create an unsorted list of transcoder info
  const refreshTranscoders = async () => {
    const transcoderInfoData = await getApiData("/transcoders/", []);
    const transcoderData = [];
    let pendingSum = 0;
    let pendingSinceLastTicketSum = 0;
    let vestedSum = 0;
    let payoutSum = 0;
    let totalEarnedSum = 0;
    let sessionSum = 0;
    let capacitySum = 0;

    // Flatten T info data
    for (const transcoder of transcoderInfoData) {
      let thisSessionSum = 0;
      let thisCapacitySum = 0;
      let thisRegions = [];
      // Init data by payout info
      for (const earningsInfo of transcoder.value_earned_balances) {
        thisRegions.push({
          orchestratorId: earningsInfo.poolOrchestrator.id,
          orchestratorName: earningsInfo.poolOrchestrator.name,
          orchestratorRegion: earningsInfo.poolOrchestrator.region,
          ethTotal: earningsInfo.ethTotal,
          ethEarned: earningsInfo.ethEarned,
          pixels: earningsInfo.pixels,
          sessions: 0,
          capacity: 0,
        });
      }
      // Fill in connection log info
      for (const connectInfo of transcoder.connect_log) {
        thisCapacitySum += connectInfo.capacity;
        let modified = false;
        for (var idx = 0; idx < thisRegions.length; idx++) {
          if (
            thisRegions[idx].orchestratorId == connectInfo.poolOrchestrator.id
          ) {
            thisRegions[idx].capacity += connectInfo.capacity;
            modified = true;
            break;
          }
        }
        if (!modified) {
          thisRegions.push({
            orchestratorId: connectInfo.poolOrchestrator.id,
            orchestratorName: connectInfo.poolOrchestrator.name,
            orchestratorRegion: connectInfo.poolOrchestrator.region,
            ethTotal: 0,
            ethEarned: 0,
            pixels: 0,
            sessions: 0,
            capacity: connectInfo.capacity,
          });
        }
      }
      // Fill in session log info
      for (const sessionInfo of transcoder.session_log) {
        thisSessionSum += sessionInfo.sessionCount;
        let modified = false;
        for (var idx = 0; idx < thisRegions.length; idx++) {
          if (
            thisRegions[idx].orchestratorId == sessionInfo.poolOrchestrator.id
          ) {
            thisRegions[idx].sessions += sessionInfo.sessionCount;
            modified = true;
            break;
          }
        }
        if (!modified) {
          thisRegions.push({
            orchestratorId: sessionInfo.poolOrchestrator.id,
            orchestratorName: sessionInfo.poolOrchestrator.name,
            orchestratorRegion: sessionInfo.poolOrchestrator.region,
            ethTotal: 0,
            ethEarned: 0,
            pixels: 0,
            sessions: sessionInfo.sessionCount,
            capacity: 0,
          });
        }
      }
      let transcoderObj = {
        address: transcoder.ethAddr.toLowerCase(),
        nickname: transcoder.nickname,
        pending: parseFloat(
          web3.utils.fromWei(transcoder.pending.toString(), "ether") || 0
        ),
        pendingSinceLastTicket: parseFloat(
          web3.utils.fromWei(transcoder.pendingSinceLastTicket.toString(), "ether") || 0
        ),
        vested: parseFloat(
          web3.utils.fromWei(transcoder.vested.toString(), "ether") || 0
        ),
        payout: parseFloat(
          web3.utils.fromWei(transcoder.payout.toString(), "ether") || 0
        ),
        totalEarned: parseFloat(
          web3.utils.fromWei(transcoder.totalEarned.toString(), "ether") || 0
        ),
        sessionSum: thisSessionSum,
        capacitySum: thisCapacitySum,
        regions: thisRegions,
      };
      sessionSum += transcoderObj.sessionSum;
      capacitySum += transcoderObj.capacitySum;
      pendingSum += transcoderObj.pending;
      pendingSinceLastTicketSum += transcoderObj.pendingSinceLastTicket;
      vestedSum += transcoderObj.vested;
      payoutSum += transcoderObj.payout;
      totalEarnedSum += transcoderObj.totalEarned;
      transcoderData.push(transcoderObj);
    }
    let poolData = {
      pendingSum,
      pendingSinceLastTicketSum,
      vestedSum,
      payoutSum,
      totalEarnedSum,
      sessionSum,
      capacitySum,
    };

    // TODO: actually check if the contents have changed instead of this shallow check
    if (transcoderData && transcoderData != transcoderInfo) {
      setTranscoders(transcoderData);
    }
    if (poolData && poolData != poolInfo) {
      setInfo(poolData);
    }
  };
  // Get ENS cache from nFrame
  const refreshENS = async () => {
    const ensData = await getAllENS();
    // TODO: actually check if the contents have changed instead of this shallow check
    if (ensData && ensData != cachedENS) {
      setENSCache(ensData);
    }
  };
  const refreshStatus = async () => {
    let statusData = await getApiData("/poolconfig/", []);
    if (!statusData || !statusData.length) {
      return;
    }
    statusData = statusData[0];
    statusData.payoutThreshold = parseFloat(
      web3.utils.fromWei(statusData?.payoutThreshold?.toString(), "ether") || 0
    );
    statusData.totalPaid = parseFloat(
      web3.utils.fromWei(statusData?.totalPaid?.toString(), "ether") || 0
    );
    // TODO: actually check if the contents have changed instead of this shallow check
    if (statusData && statusData != poolStatus) {
      setStatus(statusData);
    }
  };
  const refreshOrchestrators = async () => {
    const orchData = await getApiData(
      "/orchestrators/" +
        process.env.REACT_APP_POOL_ORCHESTRATOR_ADDRESS.toLowerCase() +
        "/",
      {}
    );
    // TODO: actually check if the contents have changed instead of this shallow check
    if (orchData && orchData != orchInfo) {
      setOrch(orchData);
    }
  };
  const refreshPayments = async () => {
    const paymentData = await getApiData("/payments/", []);
    // TODO: actually check if the contents have changed instead of this shallow check
    if (paymentData && paymentData != paymentInfo) {
      setPayments(paymentData);
    }
  };
  // Refresh pool specific data
  const refreshAllZeData = async () => {
    const statusPromise = refreshStatus();
    const transcoderPromise = refreshTranscoders();
    const orchPromise = refreshOrchestrators();
    const paymentPromise = refreshPayments();
    await Promise.all([
      statusPromise,
      transcoderPromise,
      orchPromise,
      paymentPromise,
    ]);
  };
  // Set the refresh interval for the refresh function
  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(refreshAllZeData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval]);
  // Immediately refresh all data on first render
  useEffect(() => {
    refreshAllZeData();
    refreshENS();
  }, []);

  return {
    transcoderInfo,
    poolInfo,
    poolStatus,
    orchInfo,
    cachedENS,
    paymentInfo,
  };
};

export default WithApi;
